/**
 * Who paints the graph: the graphics card when it can (config `graph { gpu #true }`, the
 * default), else the processor.
 *
 * - **GPU**: the base layer (groups, links, bundles, nodes: the many shapes) goes on a WebGL
 *   canvas placed under the window's canvas; the top layer (names, counts, handles) on the
 *   window's own canvas, with Canvas 2D, since text is what Canvas 2D does well.
 * - **CPU**: both layers on the window's canvas, with Canvas 2D.
 *
 * A graphics card the browser only emulates on the processor counts as none (see `gl/device.ts`).
 *
 * The window's canvas stays on top in both cases, so it receives every pointer event. If the
 * browser takes the graphics card back while drawing, the renderer quietly carries on with the
 * processor.
 */
import { GlPainter } from "./gl/painter";
import { clear2d, paint2d } from "./paint2d";
import type { ImageLookup, Picture } from "./shapes";

/**
 * Browser checks open the page with `?software-gpu`: their headless browser only has an
 * emulated graphics card, which the app otherwise refuses, and the GPU drawing must be checked.
 */
const ALLOW_SOFTWARE = typeof location !== "undefined" && new URLSearchParams(location.search).has("software-gpu");

/** Which processor draws: the graphics card or the main processor. */
export type RendererKind = "gpu" | "cpu";

/** Something worth a line in the log. */
export type RendererLog = (level: "info" | "warn", message: string) => void;

/** Paints pictures of the graph. */
export interface GraphRenderer {
  /** Who draws right now. */
  kind(): RendererKind;
  /** Follows the window's size (CSS pixels) and the screen's pixel density. */
  resize(width: number, height: number, dpr: number): void;
  /** Paints one picture, its images from `images`. Gives how many images it painted. */
  paint(picture: Picture, images?: ImageLookup): number;
  /**
   * Moves what was last painted on screen without painting again (a CSS transform of the
   * canvases, `null` to take it off): see deferred painting in `canvas.ts`.
   */
  shift(transform: string | null): void;
  /** Stops using the graphics card and removes its canvas. */
  dispose(): void;
}

/**
 * A renderer drawing on `canvas` (the window's Canvas 2D canvas), and, when `gpu` is asked and
 * the device allows it, on a WebGL canvas it adds just under it.
 */
export function createRenderer(canvas: HTMLCanvasElement, gpu: boolean, log: RendererLog): GraphRenderer {
  const ctx = canvas.getContext("2d");
  let size = { width: 1, height: 1, dpr: 1 };
  let painter: GlPainter | null = null;
  if (!gpu) {
    log("info", "the graph is drawn with the processor (graph gpu #false in the config)");
  } else {
    const glCanvas = document.createElement("canvas");
    glCanvas.className = "graph-gl";
    glCanvas.setAttribute("aria-hidden", "true");
    const made = GlPainter.create(glCanvas, ALLOW_SOFTWARE);
    if (made.painter) {
      painter = made.painter;
      canvas.before(glCanvas);
      log("info", `the graph is drawn with the graphics card (${painter.device})`);
    } else {
      log("warn", `the graph is drawn with the processor: ${made.why}`);
    }
  }

  /** Stops using the graphics card (lost, or the renderer is done). */
  const dropPainter = () => {
    painter?.dispose();
    painter?.canvas.remove();
    painter = null;
  };

  return {
    kind: () => (painter && !painter.lost ? "gpu" : "cpu"),
    resize(width, height, dpr) {
      size = { width, height, dpr };
      painter?.resize(width, height, dpr);
    },
    shift(transform) {
      for (const c of [canvas, painter?.canvas]) {
        if (!c) continue;
        c.style.transformOrigin = transform ? "0 0" : "";
        c.style.transform = transform ?? "";
      }
    },
    paint(picture, images) {
      if (painter?.lost) {
        dropPainter();
        log("warn", "the browser took the graphics card back: the graph is now drawn with the processor");
      }
      if (!ctx) return 0;
      clear2d(ctx, size.width, size.height, size.dpr);
      if (painter) painter.paint(picture.base);
      else paint2d(ctx, picture.base);
      return paint2d(ctx, picture.top, images);
    },
    dispose: dropPainter,
  };
}
